<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="CommentIcon"
      :label="$t('pages.driver.historyList.newComment.buttonText')"
      @click="newCommentModalOpen = true"
    />
    <export
      url="/driver_comments/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
    <a target="_BLANK" :href="zendeskDriverLink">
      <action
        :icon="LogoutIcon"
        :label="$t('pages.driver.historyList.toZendeskLink')"
      />
    </a>
  </toolbar>
  <new-comment-modal v-model:open="newCommentModalOpen" :driver="modelValue" @saved="refetch()" />
  <edit-comment-modal
    v-model:open="editCommentModalOpen"
    v-model:edit-driver-comment="editDriverComment"
    :driver="modelValue"
    @saved="refetch()"
  />
  <show-attachment-comment-modal
    v-model:open="showAttachmentDriverCommentModal"
    v-model:edit-driver-comment="editDriverComment"
    :driver="modelValue"
  />
  <div class="ml-12">
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
        :actions="actions"
        :disable-frontend-sorting="true"
        :pinnable="true"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Export from '@components/utils/toolbar/panes/Export.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Action from '@components/utils/toolbar/Action.vue';
import CommentIcon from '@images/panes/comment.svg?component';
import NewCommentModal from '@pages/drivers/driver/CommentModals/NewCommentModal.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import {collOptionsProvider, ProviderParams, useProvider} from '@utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type {
  Driver,
  HydraCollection,
  DriverComment,
  DriverCommentType,
  DriverCommentAttachment, User,
} from '@/entities/Entities';
import {logicAnd} from '@vueuse/core';
import { useFetchAction } from '@hooks/fetch';
import { useNotify } from '@utils/notifications';
import EditCommentModal from '@pages/drivers/driver/CommentModals/EditCommentModal.vue';
import { useDelete } from '@hooks/api';
import type { Video } from '@/entities/Entities';
import { useQueryRef } from '@/hooks/url';
import {Campaign} from '@/entities/Entities';
import LogoutIcon from '@images/logout.svg?component';
import {useTableFields} from '@hooks/table-configuration';
import ShowAttachmentCommentModal from '@pages/drivers/driver/CommentModals/ShowAttachmentCommentModal.vue';

interface Props {
  modelValue: Driver | null;
}

const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', 'createdAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const newCommentModalOpen = ref(false);
const editCommentModalOpen = ref(false);
const showAttachmentDriverCommentModal = ref(false);

const editDriverComment = ref<{
  driver: Driver | null;
  attachments: DriverCommentAttachment[];
  author: null | string;
  type: null | string;
  text: null | string;
  id: string | null;
  createdAt: Date | null;
}>({
  driver: props.modelValue,
  attachments: [],
  author: null,
  type: null,
  text: null,
  id: null,
  createdAt: null,
});

function openEditDriverCommentModal(driverComment: DriverComment): void {
  editDriverComment.value.author = driverComment.author?.fullName ?? 'System';
  editDriverComment.value.type = driverComment.type?.['@id'];
  editDriverComment.value.typeText = driverComment.type?.text;
  editDriverComment.value.typeDeleted = driverComment.type?.deleted;
  editDriverComment.value.text = driverComment.text;
  editDriverComment.value.attachments = driverComment.attachments ?? [];
  editDriverComment.value.id = driverComment['id'];
  editDriverComment.value.createdAt = driverComment.createdAt
    ? new Date(driverComment.createdAt)
    : new Date();
  editCommentModalOpen.value = true;
}

function openShowAttachmentDriverCommentModal(driverComment: DriverComment): void {
  editDriverComment.value.author = driverComment.author?.fullName ?? 'System';
  editDriverComment.value.type = driverComment.type?.['@id'];
  editDriverComment.value.typeText = driverComment.type?.text;
  editDriverComment.value.typeDeleted = driverComment.type?.deleted;
  editDriverComment.value.text = driverComment.text;
  editDriverComment.value.attachments = driverComment.attachments ?? [];
  editDriverComment.value.id = driverComment['id'];
  editDriverComment.value.createdAt = driverComment.createdAt
    ? new Date(driverComment.createdAt)
    : new Date();
  showAttachmentDriverCommentModal.value = true;
}

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.historyFromDriver', {
    driver: props.modelValue?.user?.fullName,
  });
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  driver: props.modelValue?.id ?? '',
  'order[pinned]': 'DESC',
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const [deleteDriverComment, { loading: deleting }] = useDelete();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const [execPin, { loading: loadingPin }] = useFetchAction();

const zendeskDriverLink = computed(() => `${window.env.VITE_ZENDESK_PROFILE_SEARCH_URL || import.meta.env.VITE_ZENDESK_PROFILE_SEARCH_URL}${props.modelValue?.user?.email}`)

const pinDriverComment = async (driverComment: DriverComment, pinned: boolean) => {
  try {
    await execPin(`/driver_comments/${driverComment.id}`, {
      method: 'PUT',
      body: {
        pinned,
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.driver.historyList.pinned.success'),
    });

    await refetch();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.driver.historyList.pinned.error'),
    });
  }
}

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const actions = [
  {
    title: t('pages.driver.historyList.actions.pin'),
    action: (item: DriverComment) => pinDriverComment(item, true),
    condition: (item: DriverComment) => !item.pinned,
  },
  {
    title: t('pages.driver.historyList.actions.unpin'),
    action: (item: DriverComment) => pinDriverComment(item, false),
    condition: (item: DriverComment) => item.pinned,
  },
  {
    title: t('pages.driver.historyList.actions.edit'),
    action: (item: DriverComment) => openEditDriverCommentModal(item),
  },
  {
    title: t('pages.driver.historyList.actions.showAttachments'),
    action: (item: DriverComment) => openShowAttachmentDriverCommentModal(item),
  },
  {
    title: t('pages.driver.historyList.actions.delete'),
    action: async (item: DriverComment) => {
      if (await deleteDriverComment(`/driver_comments/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
  },
];

// TODO: Action-Column with context-menu
const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.driver.historyList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'createdAt',
    title: t('date'),
    group: t('pages.driver.historyList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.driver.historyList.filterGroups.general'),
      options: {
        label: t('date'),
      },
    },
  },
  {
    key: 'type.text',
    title: t('driverCommentType.text'),
    group: t('pages.driver.historyList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'dropdown',
      group: t('pages.driver.historyList.filterGroups.general'),
      options: {
        options: collOptionsProvider<DriverCommentType>('/driver_comment_types', {
          sort: {
            sortKey: 'text',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.text,
          value: value.text,
        })),
        label: t('driverCommentType.text'),
      },
    },
  },
  {
    key: 'author.fullName',
    title: t('driverComment.author.name'),
    group: t('pages.driver.historyList.fieldGroups.general'),
    sortable: true,
    visible: true,
    computed: (v: DriverComment) => v.author === null ? t('systemUser') : v.author?.fullName,
    filter: {
      key: 'author',
      type: 'dropdown',
      group: t('pages.driver.historyList.filterGroups.general'),
      options: {
        label: t('driverComment.author.name'),
        searchable: true,
        clearOnSelect: true,
        options: async () => {
          let options = await collOptionsProvider<User>('/admins', {}, (value) => ({
            label: value.fullName,
            value: value.id,
          }))();
          options.push({
            label: t('systemUser'),
            value: 'null',
          });
          return options;
        },
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['author'];
            m['exists[author]'] = false;
          } else {
            delete m['exists[author]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[author]' in m) {
            return 'null';
          }
          return m[key];
        },
      },
    },
  },
  {
    key: 'text',
    title: t('driverComment.text'),
    group: t('pages.driver.historyList.fieldGroups.general'),
    sortable: true,
    visible: true,
    isHtml: true,
    class: 'a-colored',
    formatter: (v: string) => v.replace(/#\d+/,
      (oldVal: string) => `<a target="_BLANK" href="${window.env.VITE_ZENDESK_TICKET_URL || import.meta.env.VITE_ZENDESK_TICKET_URL}${oldVal.replace('#', '')}">${oldVal}</a>`
    ),
    filter: {
      type: 'text',
      group: t('pages.driver.historyList.filterGroups.general'),
      options: {
        label: t('driverComment.text'),
      },
    },
  },
  {
    key: 'attachments',
    title: t('driverComment.attachments'),
    visible: true,
    sortable: false,
    formatter: (v: DriverCommentAttachment[]) => v.length,
  }
];

const visibleColumns = useTableFields(
    columns,
);

const blockRequest = computed(() => !props.modelValue);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<DriverComment>>('/driver_comments', providerParams, {
  disabled: blockRequest,
  immediate: true,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const notify = useNotify();

const [exec, { loading: loadingDelete }] = useFetchAction();

const loading = computed(() => loadingTable.value || loadingDelete.value || loadingPin.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}

::v-deep(.a-colored) {
  a {
    color: var(--color-red-130);

    &:hover {
      color: var(--color-red-80);
    }
  }
}
</style>
